import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState, useCallback } from "react";
import { useRouter } from "expo-router";
import { useSelector } from "react-redux";
import axios from "axios";
import { Text } from "./components/Text";
import NumericKeypad from "./components/PickupDriveThru/NumericKeypad";
import OrderField from "./components/PickupDriveThru/OrderField";
import Header from "./components/PickupDriveThru/Header";
import FooterButton from "./components/PickupDriveThru/FooterButton";
import { colors } from "./constants/styles";
import { useTranslateText } from "./utils";
import { isUserAuthenticated } from "./selectors/user";
import url, { getFullURL } from "./api";
import { getOrderIdentifierPrefix } from "./constants/orderIdentifier";

const ORDER_IDENTIFIER_PREFIX = getOrderIdentifierPrefix();
const MAX_FIELD_LENGTH = 10;

type ActiveField = "orderIdentifier" | "orderPin";

type ErrorState = {
  message: string;
  canRetry: boolean;
} | null;

export default function PickupDriveThru() {
  const router = useRouter();
  const { t, locale } = useTranslateText();
  const authToken = useSelector(isUserAuthenticated);

  const [activeField, setActiveField] =
    useState<ActiveField>("orderIdentifier");
  const [orderIdentifier, setOrderIdentifier] = useState("");
  const [orderPin, setOrderPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorState>(null);

  const setActiveValue =
    activeField === "orderIdentifier" ? setOrderIdentifier : setOrderPin;
  const canSubmit =
    orderIdentifier.length > 0 && orderPin.length > 0 && !isLoading;

  const handleSubmit = useCallback(async () => {
    if (!authToken) {
      setError({
        message: t("tpos.pickupDriveThru.errors.unauthorized"),
        canRetry: false,
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    const orderId = `${ORDER_IDENTIFIER_PREFIX}${orderIdentifier}`;
    const endpoint = url.customerArrival(orderId);
    const fullUrl = getFullURL(
      endpoint.url,
      endpoint.prefix,
      endpoint.apiVersion,
    );

    try {
      await axios.post(
        fullUrl,
        {
          customerArrival: {
            backupPin: orderPin,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            "ALBAIK-Auth-Token": authToken,
            "Accept-Language": locale,
          },
        },
      );

      router.replace("/pickup-drive-thru-verified");
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const data = err.response?.data as {
          message?: string;
          errors?: string[];
        };

        // Network error (no response from server)
        if (!err.response) {
          setError({
            message: t("tpos.pickupDriveThru.errors.networkError"),
            canRetry: true,
          });
        } else if (status === 401) {
          setError({
            message: t("tpos.pickupDriveThru.errors.unauthorized"),
            canRetry: false,
          });
        } else if (status === 404) {
          setError({
            message: t("tpos.pickupDriveThru.errors.orderNotFound"),
            canRetry: true,
          });
        } else if (status === 422) {
          const isAlreadyCheckedIn = data?.message === "Already checked-in";
          if (isAlreadyCheckedIn) {
            setError({
              message: t("tpos.pickupDriveThru.errors.alreadyCheckedIn"),
              canRetry: false,
            });
          } else {
            const errorMessages = data?.errors?.join(", ") || data?.message;
            setError({
              message:
                errorMessages ||
                t("tpos.pickupDriveThru.errors.validationFailed"),
              canRetry: true,
            });
          }
        } else {
          setError({
            message: t("tpos.pickupDriveThru.errors.unknown"),
            canRetry: true,
          });
        }
      } else {
        // Non-axios error (e.g., programming error)
        console.error("Customer arrival error:", err);
        setError({
          message: t("tpos.pickupDriveThru.errors.unknown"),
          canRetry: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [orderIdentifier, orderPin, authToken, locale, router, t]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <View style={styles.container} testID="pickupDriveThruContainer">
      <Header />
      <View style={styles.content}>
        <Text style={styles.enterManually}>
          {t("tpos.pickupDriveThru.enterManually")}
        </Text>
        {error && (
          <View
            style={[
              styles.errorContainer,
              !error.canRetry && styles.errorContainerNoRetry,
            ]}
            testID="errorMessage"
          >
            <Text style={styles.errorText}>{error.message}</Text>
          </View>
        )}
        <View style={styles.entryArea}>
          <NumericKeypad
            deleteLabel={t("tpos.pickupDriveThru.delete")}
            onDigitPress={(digit) => {
              clearError();
              setActiveValue((value) =>
                value.length < MAX_FIELD_LENGTH ? value + digit : value,
              );
            }}
            onDeletePress={() => {
              clearError();
              setActiveValue((value) => value.slice(0, -1));
            }}
          />
          <View style={styles.fields}>
            <OrderField
              testID="orderIdentifierField"
              label={t("tpos.pickupDriveThru.posOrderIdentifier")}
              prefix={ORDER_IDENTIFIER_PREFIX}
              value={orderIdentifier}
              isActive={activeField === "orderIdentifier"}
              onPress={() => setActiveField("orderIdentifier")}
            />
            <OrderField
              testID="orderPinField"
              label={t("tpos.pickupDriveThru.orderPin")}
              value={orderPin}
              isActive={activeField === "orderPin"}
              onPress={() => setActiveField("orderPin")}
            />
          </View>
        </View>
      </View>
      <FooterButton
        testID="pickupDriveThruSubmitButton"
        text={
          isLoading ? (
            <ActivityIndicator color={colors.white} testID="submitSpinner" />
          ) : (
            t("tpos.pickupDriveThru.submit")
          )
        }
        disabled={!canSubmit || (error !== null && !error.canRetry)}
        onPress={handleSubmit}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  content: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 32,
    gap: 24,
  },
  enterManually: {
    fontSize: 48,
    fontWeight: "bold",
    color: colors.maroon,
  },
  errorContainer: {
    backgroundColor: colors.errorBackground,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    maxWidth: 500,
  },
  errorContainerNoRetry: {
    backgroundColor: colors.warningBackground,
  },
  errorText: {
    fontSize: 16,
    color: colors.errorText,
    textAlign: "center",
  },
  entryArea: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 48,
  },
  fields: {
    gap: 40,
  },
});
